import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Plus, Trash2, Check, Send, Power, CalendarClock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const INP = "bg-secondary/60 border border-border rounded-md px-2.5 py-1.5 text-sm outline-none focus:border-[#17A2B8]";
const BASES = [["activite", "Activité quotidienne (distance, marche, ralenti…)"], ["exces", "Excès de vitesse"], ["synthese_alertes", "Synthèse des alertes"], ["energy", "Carburant / Énergie / Coûts"]];
const FREQ = { daily: "Quotidien", weekly: "Hebdomadaire (lundi)", monthly: "Mensuel" };
const TZ_LABEL = (tz) => (tz === "Pacific/Tahiti" ? "Pacific/Tahiti" : "Pacific/Noumea");

const empty = () => ({ name: "", base_key: "activite", frequency: "daily", hour: 7, day_of_month: 1, format: "HTML", company_id: "", vehicle_ids: [], recipients: "", enabled: true });

export default function ScheduledReportsPanel() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const load = useCallback(() => api.get("/scheduled-reports").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, [load]);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const startNew = () => setForm(empty());
  const startEdit = (r) => setForm({ ...r, recipients: (r.recipients || []).join(", "), company_id: r.company_id || "", vehicle_ids: r.vehicle_ids || [] });

  // Changement de société : on ne conserve JAMAIS une sélection de véhicule hors de la nouvelle société.
  const onCompanyChange = (cid) => setForm((s) => ({ ...s, company_id: cid, vehicle_ids: [] }));

  const companyVehicles = form ? vehicles.filter((v) => v.company_id === form.company_id) : [];
  const selTz = TZ_LABEL((companies.find((c) => c.id === (form && form.company_id)) || {}).timezone);

  const toggleVeh = (vid) => setForm((s) => {
    const set = new Set(s.vehicle_ids || []);
    set.has(vid) ? set.delete(vid) : set.add(vid);
    return { ...s, vehicle_ids: [...set] };
  });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    if (!form.company_id) { toast.error("Sélectionnez la société"); return; }
    const recipients = form.recipients.split(",").map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) { toast.error("Au moins un destinataire"); return; }
    const payload = {
      name: form.name, base_key: form.base_key, frequency: form.frequency, hour: Number(form.hour),
      day_of_month: Number(form.day_of_month) || 1, format: form.format || "HTML",
      company_id: form.company_id, vehicle_ids: form.vehicle_ids || [], recipients, enabled: form.enabled,
    };
    try {
      if (form.id) await api.put(`/scheduled-reports/${form.id}`, payload);
      else await api.post("/scheduled-reports", payload);
      toast.success("Rapport planifié enregistré");
      setForm(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur (droits admin requis ?)"); }
  };

  const del = async (id) => { try { await api.delete(`/scheduled-reports/${id}`); load(); } catch { toast.error("Erreur"); } };
  const toggle = async (r) => {
    if (r.needs_config) { toast.error("Associez d'abord ce rapport à une société."); startEdit(r); return; }
    try { await api.put(`/scheduled-reports/${r.id}`, { name: r.name, base_key: r.base_key, frequency: r.frequency, hour: r.hour, day_of_month: r.day_of_month || 1, format: r.format || "HTML", company_id: r.company_id, vehicle_ids: r.vehicle_ids || [], recipients: r.recipients, enabled: !r.enabled }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const runNow = async (r) => {
    toast.info("Envoi en cours…");
    try { const res = await api.post(`/scheduled-reports/${r.id}/run`); toast.success(res.data?.status === "sent" ? `Email envoyé à ${(res.data.recipients || []).join(", ")}` : "Aucun destinataire"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Échec de l'envoi"); }
  };

  return (
    <div data-testid="scheduled-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground flex items-center gap-2"><CalendarClock className="w-4 h-4 brand-teal" /> Recevez ce rapport par email automatiquement.</p>
        {!form && <button onClick={startNew} data-testid="sched-add" className="flex items-center gap-1 bg-[#17A2B8] hover:bg-[#1391a5] text-white text-xs font-semibold px-3 py-1.5 rounded-md"><Plus className="w-4 h-4" /> Nouveau</button>}
      </div>

      {form ? (
        <div className="space-y-3 border border-border rounded-md p-4 bg-secondary/20" data-testid="sched-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs text-muted-foreground flex flex-col gap-1">Nom
              <input className={INP} value={form.name} onChange={(e) => setF("name", e.target.value)} data-testid="sched-name" placeholder="Ex: Activité flotte" />
            </label>
            <label className="text-xs text-muted-foreground flex flex-col gap-1">Société
              <select className={INP} value={form.company_id} onChange={(e) => onCompanyChange(e.target.value)} data-testid="sched-company">
                <option value="">— Sélectionner —</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="text-xs text-muted-foreground flex flex-col gap-1">Type de rapport
              <select className={INP} value={form.base_key} onChange={(e) => setF("base_key", e.target.value)} data-testid="sched-base">
                {BASES.map((b) => <option key={b[0]} value={b[0]}>{b[1]}</option>)}
              </select>
            </label>
            <label className="text-xs text-muted-foreground flex flex-col gap-1">Fréquence
              <select className={INP} value={form.frequency} onChange={(e) => setF("frequency", e.target.value)} data-testid="sched-freq">
                <option value="daily">Quotidien</option><option value="weekly">Hebdomadaire (lundi)</option><option value="monthly">Mensuel</option>
              </select>
            </label>
            <label className="text-xs text-muted-foreground flex flex-col gap-1">Heure d'envoi locale — {selTz}
              <input type="number" min="0" max="23" className={INP} value={form.hour} onChange={(e) => setF("hour", e.target.value)} data-testid="sched-hour" />
            </label>
            <label className="text-xs text-muted-foreground flex flex-col gap-1">Format d'envoi
              <select className={INP} value={form.format} onChange={(e) => setF("format", e.target.value)} data-testid="sched-format">
                <option value="HTML">HTML (dans l'email)</option><option value="PDF">PDF (pièce jointe)</option>
              </select>
            </label>
            {form.frequency === "monthly" && (
              <label className="text-xs text-muted-foreground flex flex-col gap-1">Jour du mois (1–28) — période : mois civil précédent
                <input type="number" min="1" max="28" className={INP} value={form.day_of_month} onChange={(e) => setF("day_of_month", e.target.value)} data-testid="sched-dom" />
              </label>
            )}
          </div>

          {form.company_id && (
            <div className="text-xs text-muted-foreground flex flex-col gap-1" data-testid="sched-vehicles">
              <span>Véhicules ({form.vehicle_ids?.length ? `${form.vehicle_ids.length} sélectionné(s)` : "tous les véhicules de la société"})</span>
              <div className="max-h-40 overflow-y-auto border border-border rounded-md p-2 grid grid-cols-1 sm:grid-cols-2 gap-1 bg-secondary/40">
                {companyVehicles.length === 0 && <span className="text-muted-foreground px-1">Aucun véhicule dans cette société.</span>}
                {companyVehicles.map((v) => (
                  <label key={v.id} className="flex items-center gap-2 px-1 py-0.5 cursor-pointer" data-testid={`sched-veh-${v.id}`}>
                    <input type="checkbox" checked={(form.vehicle_ids || []).includes(v.id)} onChange={() => toggleVeh(v.id)} />
                    <span className="truncate">{v.name} · {v.plate}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <label className="text-xs text-muted-foreground flex flex-col gap-1">Destinataires (séparés par des virgules)
            <input className={INP} value={form.recipients} onChange={(e) => setF("recipients", e.target.value)} data-testid="sched-recipients" placeholder="contact@boomerang.nc, ..." />
          </label>
          <label className="text-xs text-muted-foreground flex items-center gap-2"><input type="checkbox" checked={form.enabled} onChange={(e) => setF("enabled", e.target.checked)} data-testid="sched-enabled" /> Planification active</label>
          <div className="flex items-center gap-2">
            <button onClick={save} data-testid="sched-save" className="bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold px-5 py-2 rounded-md text-sm flex items-center gap-1"><Check className="w-4 h-4" /> Enregistrer</button>
            <button onClick={() => setForm(null)} data-testid="sched-cancel" className="text-muted-foreground px-4 py-2 text-sm hover:text-foreground">Annuler</button>
          </div>
        </div>
      ) : (
        <div className="border border-border rounded-md divide-y divide-border" data-testid="sched-list">
          {list.length === 0 && <div className="px-4 py-8 text-center text-sm text-muted-foreground">Aucun rapport planifié.</div>}
          {list.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-4 py-3" data-testid={`sched-item-${r.id}`}>
              <button onClick={() => toggle(r)} data-testid={`sched-toggle-${r.id}`} className={`p-1.5 rounded-md ${r.enabled ? "text-[#10B981] bg-[#10B981]/15" : "text-muted-foreground bg-secondary"}`}><Power className="w-4 h-4" /></button>
              <button onClick={() => startEdit(r)} data-testid={`sched-edit-${r.id}`} className="flex-1 text-left min-w-0">
                <div className="font-semibold text-sm truncate flex items-center gap-2">
                  {r.name}
                  {r.needs_config && <span data-testid={`sched-needs-config-${r.id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#F5821F] bg-[#F5821F]/15 px-1.5 py-0.5 rounded"><AlertTriangle className="w-3 h-3" /> À configurer</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {r.company_name ? `${r.company_name} · ` : ""}{FREQ[r.frequency]}{r.frequency === "monthly" ? ` (jour ${r.day_of_month || 1})` : ""} · {String(r.hour).padStart(2, "0")}:00 {TZ_LABEL(r.timezone)} · {(r.format || "HTML") === "PDF" ? "PDF" : "HTML"}
                  {(r.vehicle_ids || []).length ? ` · ${r.vehicle_ids.length} véh.` : ""} · {(r.recipients || []).join(", ")}
                  {r.last_sent ? ` · dernier envoi ${r.last_sent.slice(0, 16).replace("T", " ")}` : ""}
                </div>
              </button>
              <button onClick={() => runNow(r)} data-testid={`sched-run-${r.id}`} className="text-[#17A2B8] p-1.5 hover:bg-[#17A2B8]/15 rounded flex items-center gap-1 text-xs font-semibold"><Send className="w-4 h-4" /> Envoyer</button>
              <button onClick={() => del(r.id)} data-testid={`sched-del-${r.id}`} className="text-[#EF4444] p-1.5 hover:bg-[#EF4444]/15 rounded"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
